import { FormGroup, Label } from "@xola/ui-kit";
import clsx from "clsx";
import { isStripeEnabled } from "common";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Payment, PaymentMethod, RemoteCard, SellerPaymentMethod, Traveler } from "types";
import { useSeller } from "../../hooks/useAccount";
import { useResource } from "../../hooks/useResource";
import { hasCustomPaymentGateway, isAuthorizeNetEnabled } from "../../models/Seller";
import { useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { PaymentMethodButtons } from "../PurchaseModification/PaymentMethodButtons";
import { TravelerCardList } from "../PurchaseModification/TravelerCardList";
import { AuthorizeNetCardInput } from "./AuthorizeNetCardInput";
import { CashInput } from "./CashInput";
import { EmvInput } from "./EmvInput";
import { PaymentInputRef, TokenizePaymentParams } from "./PaymentInputRef";
import { StripeCardInput } from "./StripeCardInput";
import { getCompatibleCardType } from "@/helpers/rawCardHelper";

interface PaymentInputProps {
    value?: Payment;
    amount?: number;
    onChange: (value: Payment) => void;
    organizer?: ID;
    onError: (error: string | null) => void;
    isChargeCard?: boolean;
    onChargeCard?: (value: boolean) => void;
    excludePaymentMethods?: Array<PaymentMethod | ID>;
    onAdditionalDetailsSubmit: (tokenizePaymentParams: TokenizePaymentParams) => Promise<void>;
    onAdditionalDetailsSubmitSuccess?: () => void;
    showDoNotChargeCardSwitch?: boolean;
    isPartnerBooking?: boolean;
}

export const PaymentInput = forwardRef<PaymentInputRef, PaymentInputProps>(
    (
        {
            value: initialValue,
            amount,
            onChange,
            organizer: organizerId,
            onError,
            isChargeCard,
            onChargeCard,
            excludePaymentMethods,
            showDoNotChargeCardSwitch,
            onAdditionalDetailsSubmit,
            onAdditionalDetailsSubmitSuccess,
            isPartnerBooking,
        }: PaymentInputProps,
        ref,
    ) => {
        const payment = useCreatePurchaseStore((state) => state.paymentLineItem.payment);
        const value = initialValue ?? payment;
        const seller = useSeller();
        const [selectedCard, setSelectedCard] = useState<RemoteCard | null>(null);
        const [error, setError] = useState<string | null>(null);
        const isManual = useCreatePurchaseStore((state) => !state.isEMV);
        const cashInputRef = useRef<PaymentInputRef>(null);
        const cardInputRef = useRef<PaymentInputRef>(null);
        const emvRef = useRef<PaymentInputRef>(null);
        const fetchParams = { staleTime: 1000 };

        const { data: organizer } = useResource<Traveler>(
            ["users", organizerId],
            { private: true },
            { ...fetchParams, enabled: !isPartnerBooking },
        );

        useEffect(() => {
            if (!isManual) {
                // Reset the error flag when you switch to EMV because previous errors do not apply
                setError(null);
            }
        }, [isManual, setError]);

        useEffect(() => {
            onError(error);
        }, [error]); // eslint-disable-line react-hooks/exhaustive-deps

        useEffect(() => {
            if (!organizer) {
                setSelectedCard(null);
            }
        }, [organizer]);

        useImperativeHandle(
            ref,
            () => ({
                async prepare() {
                    if (value.method === PaymentMethod.CASH && cashInputRef.current) {
                        return cashInputRef.current.prepare?.();
                    }

                    if (value.method === PaymentMethod.CREDIT_CARD && cardInputRef.current) {
                        return cardInputRef.current.prepare?.();
                    }

                    return true;
                },

                async confirm(params) {
                    if (value.method === PaymentMethod.CREDIT_CARD) {
                        if (value.paymentIntentMethods?.includes("card_present") && !isManual) {
                            return emvRef.current?.confirm?.(params);
                        }

                        return cardInputRef.current?.confirm?.(params);
                    }

                    // No-op
                },

                async tokenize() {
                    if (value.method === PaymentMethod.CREDIT_CARD) {
                        return cardInputRef.current?.tokenize() ?? null;
                    }

                    return null;
                },

                closeEMVModal() {
                    return emvRef.current?.closeEMVModal!();
                },

                getError() {
                    return error ?? undefined;
                },

                onPaymentError(error: string) {
                    return cardInputRef.current?.onPaymentError?.(error);
                },
            }),
            [error, value, isManual],
        );

        const handleMethodChange = (sellerPaymentMethod: SellerPaymentMethod) => {
            setError(null);
            if (sellerPaymentMethod.id === PaymentMethod.CREDIT_CARD && selectedCard) {
                onChange({ method: sellerPaymentMethod.id, card: selectedCard.id });
            } else {
                onChange({ method: sellerPaymentMethod.id });
            }
        };

        const handleSelectedCardChange = (card: RemoteCard | null) => {
            if (
                card &&
                hasCustomPaymentGateway(seller) &&
                !seller.acceptedCards.includes(getCompatibleCardType(card.brand.toLowerCase()))
            ) {
                setError("is not acceptable");
            } else {
                setError(null);
            }

            setSelectedCard(card);
            onChange({ method: PaymentMethod.CREDIT_CARD, card: card?.id });
        };

        const paymentMethodValue = { id: value.method, name: "" };
        return (
            <>
                <PaymentMethodButtons
                    value={paymentMethodValue}
                    excludePaymentMethods={excludePaymentMethods}
                    isChargeCard={isChargeCard}
                    showDoNotChargeCardSwitch={showDoNotChargeCardSwitch}
                    isOtherSellerPurchase={isPartnerBooking}
                    onChargeCard={onChargeCard}
                    onChange={handleMethodChange}
                />

                <CashInput ref={cashInputRef} amount={amount} value={value} />

                {value.method === PaymentMethod.CREDIT_CARD ? (
                    <FormGroup>
                        {(error || isManual) && (
                            <Label className={clsx(error && "!text-danger")}>Credit Card {error}</Label>
                        )}

                        {isManual && (
                            <TravelerCardList
                                organizer={organizer}
                                value={selectedCard}
                                isPartnerBooking={isPartnerBooking}
                                onChange={handleSelectedCardChange}
                            >
                                {isAuthorizeNetEnabled(seller) && (
                                    <AuthorizeNetCardInput
                                        ref={cardInputRef}
                                        className={clsx(organizer?.creditCards?.length && "rounded-r-none")}
                                        onAdditionalDetailsSubmit={onAdditionalDetailsSubmit}
                                        onError={setError}
                                    />
                                )}
                                {isStripeEnabled(seller) && (
                                    <StripeCardInput
                                        ref={cardInputRef}
                                        className={clsx(organizer?.creditCards?.length && "rounded-r-none")}
                                        organizer={organizer}
                                        onAdditionalDetailsSubmitSuccess={onAdditionalDetailsSubmitSuccess}
                                        onError={setError}
                                    />
                                )}
                            </TravelerCardList>
                        )}
                        {isStripeEnabled(seller) ? <EmvInput ref={emvRef} /> : null}
                    </FormGroup>
                ) : null}
            </>
        );
    },
);
